import React, { useEffect, useMemo, useState } from 'react';
import BigNumber from 'bignumber.js';
import { ChainId, Token, Tokens, NETWORK_CONFIG } from '../../../../constants';
import { BridgeStrategy, BridgeConfig } from '../../types';
import BridgeToken from '../../BridgeToken';
import useTokenApprove from '@/hooks/use-token-approve';
import useTokenBalance from '@/hooks/use-token-balance';
import useGoatTokenOFT from '@/hooks/useGoatTokenOFT';
import AlertFunc from '@/utils/components/alert';
import ModalFunc from '@/utils/components/modal';
import user from '@/stores/user';
import { observer } from 'mobx-react';

interface GoatToMetisBridgeProps {
    fromChainId: ChainId;
    toChainId: ChainId;
    currentToken: Token;
    setCurrentToken: (token: Token) => void;
    onTransactionSuccess: (
        tx: string,
        amount: BigNumber,
        tokenName: string,
    ) => void;
}

function GoatToMetisBridge({
    fromChainId,
    toChainId,
    currentToken,
    setCurrentToken,
    onTransactionSuccess,
}: GoatToMetisBridgeProps) {
    const { connected, address: walletAddress } = user;

    const [inputAmount, setInputAmount] = useState(new BigNumber(0));
    const [transferring, setTransferring] = useState(false);
    const [transFeeInfo, setTransFeeInfo] = useState<{
        fee_num: number;
        servicesFee: number;
    }>({
        fee_num: 0,
        servicesFee: 0,
    });
    const [loadingTransFee, setLoadingTransFee] = useState(false);
    const [inputErrorMessage, setInputErrorMessage] = useState<
        string | undefined
    >(undefined);

    const { approved, approveUserTokenForSpender } = useTokenApprove(
        currentToken,
        inputAmount,
        fromChainId,
        toChainId,
    );

    const { estimateOFTFee, sendOFTToMetis } = useGoatTokenOFT();

    const {
        balance: currentTokenBalance,
        getBalance: updateCurrentTokenBalance,
    } = useTokenBalance(
        (Tokens[currentToken] as any)[fromChainId]?.address || '',
        undefined,
        {
            intervalTime: 10000,
            isOft: !!(Tokens[currentToken] as any)[fromChainId]?.oft,
        },
    );

    const { balance: gasTokenBalance } = useTokenBalance('');

    // Bridge configuration for GOAT -> Metis
    const bridgeConfig: BridgeConfig = useMemo(() => {
        return {
            etaText: `From ${NETWORK_CONFIG[fromChainId].name} to ${NETWORK_CONFIG[toChainId].name}: less than 10 minutes`,
            etaTooltip: `From ${NETWORK_CONFIG[fromChainId].name} to ${NETWORK_CONFIG[toChainId].name}: less than 10 minutes`,
            timeDisplay: 'Less than 10 minutes',
            showProtocolFee: false,
            showDate: false,
            gasTokenName: 'BTC',
        };
    }, [fromChainId, toChainId]);

    useEffect(() => {
        setInputAmount(new BigNumber(0));
    }, [fromChainId]);

    // Validation logic for GOAT -> Metis
    useEffect(() => {
        if (inputAmount.isGreaterThan(currentTokenBalance)) {
            setInputErrorMessage('Insufficient Balance');
        } else if (inputAmount.isGreaterThan(0)) {
            // For GOAT -> Metis, check if enough BTC is reserved
            const isGasToken = false;
            getLeftAmount().then((leftAmount) => {
                if (leftAmount) {
                    if (
                        gasTokenBalance
                            .minus(isGasToken ? inputAmount : 0)
                            .isLessThan(leftAmount)
                    ) {
                        setInputErrorMessage(
                            `You should reserve at least ${
                                leftAmount?.toFixed() || '-'
                            } BTC as the bridging cost to ${
                                NETWORK_CONFIG[toChainId].name
                            }.`,
                        );
                    } else {
                        setInputErrorMessage(undefined);
                    }
                }
            });
        } else {
            setInputErrorMessage(undefined);
        }
    }, [inputAmount, currentTokenBalance, gasTokenBalance, currentToken]);

    async function transfer(recipientAddress: string): Promise<string | false> {
        setTransferring(true);

        let tx: any = false;

        try {
            const tokenDetail = (Tokens[currentToken] as any)?.[fromChainId];

            tx = await sendOFTToMetis({
                tokenAddress: tokenDetail?.address,
                decimals: tokenDetail?.decimals || 18,
                amount: inputAmount,
                recipientAddress: recipientAddress,
            });

            if (tx) {
                onTransactionSuccess(
                    tx,
                    inputAmount,
                    (Tokens[currentToken] as any)[fromChainId]?.symbol || '',
                );
                setInputAmount(new BigNumber(0));
            }
        } catch (error) {
            console.error('GOAT to Metis bridge transfer error:', error);
        }

        setTransferring(false);
        return tx;
    }

    async function getTransFeeDetail(
        recipientAddress: string,
        amount: BigNumber,
    ) {
        if (!approved || transferring) return;

        if (
            amount.isGreaterThan(0) &&
            currentTokenBalance.isGreaterThanOrEqualTo(amount)
        ) {
            try {
                setLoadingTransFee(true);

                const tokenDetail = (Tokens[currentToken] as any)?.[
                    fromChainId
                ];

                const feeEstimate = await estimateOFTFee({
                    tokenAddress: tokenDetail?.address,
                    decimals: tokenDetail?.decimals || 18,
                    amount: amount,
                    recipientAddress: recipientAddress,
                });

                if (feeEstimate) {
                    setTransFeeInfo({
                        fee_num: parseFloat(feeEstimate.feeInEther) || 0,
                        servicesFee: 0,
                    });
                } else {
                    setTransFeeInfo({
                        fee_num: 0,
                        servicesFee: 0,
                    });
                }
            } catch (e) {
                console.error('getTransFeeDetail error');
                console.error(e);
            }
            setLoadingTransFee(false);
        }
    }

    async function getLeftAmount(): Promise<BigNumber | undefined> {
        if (!currentTokenBalance) return undefined;

        try {
            const tokenDetail = (Tokens[currentToken] as any)?.[fromChainId];

            const feeEstimate = await estimateOFTFee({
                tokenAddress: tokenDetail?.address,
                decimals: tokenDetail?.decimals || 18,
                amount: currentTokenBalance,
                recipientAddress: walletAddress || '',
            });

            if (feeEstimate) {
                return new BigNumber(feeEstimate.feeInEther);
            }
        } catch (e) {
            console.error('getLeftAmount LayerZero estimation error:', e);
        }

        return undefined;
    }

    async function handleMax(recipientAddress: string) {
        if (!currentTokenBalance) return;

        const isGasToken = false;

        if (isGasToken && currentTokenBalance.isGreaterThan(0)) {
            try {
                const tokenDetail = (Tokens[currentToken] as any)?.[
                    fromChainId
                ];

                const feeEstimate = await estimateOFTFee({
                    tokenAddress: tokenDetail?.address,
                    decimals: tokenDetail?.decimals || 18,
                    amount: currentTokenBalance,
                    recipientAddress: recipientAddress,
                });

                if (feeEstimate) {
                    const layerzeroFee = new BigNumber(feeEstimate.feeInEther);
                    const leftAmount = currentTokenBalance
                        .minus(layerzeroFee)
                        .minus(0.01);

                    if (leftAmount.isLessThanOrEqualTo(0)) {
                        ModalFunc({
                            type: 'sorry',
                            title: 'Sorry',
                            text: `The current ${
                                Tokens[currentToken][fromChainId]?.symbol || ''
                            } Token balance ${currentTokenBalance.toFixed(
                                3,
                            )} on your account is not sufficient to support this execution. Please leave at least ${layerzeroFee.toFixed(
                                6,
                            )} ${
                                Tokens[currentToken][fromChainId]?.symbol || ''
                            } to support this execution`,
                            buttonText: 'OK',
                        });
                    } else {
                        setInputAmount(leftAmount);

                        AlertFunc({
                            type: 'info',
                            text: `Your estimated gas fee has been subtracted from your balance.`,
                            duration: 10000,
                        });
                    }
                }
            } catch (e) {
                console.error(e);
            }
        } else {
            setInputAmount(currentTokenBalance);
        }
    }

    const bridgeStrategy: BridgeStrategy = {
        currentToken,
        setCurrentToken,
        currentTokenBalance,
        updateTokenBalance: updateCurrentTokenBalance,
        inputAmount,
        setInputAmount,
        inputErrorMessage,
        approved,
        approveToken: approveUserTokenForSpender,
        transfer,
        transferring,
        estimateFee: getTransFeeDetail,
        transFeeInfo,
        loadingTransFee,
        handleMax,
        getLeftAmount,
        fromChainId,
        toChainId,
        bridgeConfig,
    };

    return (
        <BridgeToken
            strategy={bridgeStrategy}
            connected={connected}
            walletAddress={walletAddress}
            onConnect={() => user.toggleConnectModal()}
        />
    );
}

export default observer(GoatToMetisBridge);
